import React, { useEffect } from 'react';
import Overlay from '@core/js/components/common/Overlay';
import { dispatcher } from 'dispatcher';

/**
 * Handles click outside of modals/popups/sidebars etc., to close it, also may be as overlay with backgoround / may handle other modals opening
 * @param {boolean} active Should overlay be active or not
 * @param {function} closeHandler Function to close modal/popup/sidebar on click outside
 * @param {boolean} isOverlay Grey out background & prevent body scrolling | if false, modalRef should be passed.
 * @param {Ref} modalRef Modal ref needs to don't close modal when click outside of modal
 * @param {boolean} closeOnModalEvent Should modal be closed in case of any other modal event is fired. f.e. in case of bonus accural modal
 * @returns {null|ReactComponent} If {isOverlay} is true, returns {ReactComponent}, otherwise {null}
 */

const useClickOutside = (
    active: boolean,
    closeHandler: () => void,
    isOverlay = true,
    modalRef: React.RefObject<HTMLElement> | null = null,
    closeOnModalEvent = true
) => {
    useEffect(() => {
        const cleanup: (() => void)[] = [];
        if (closeOnModalEvent) {
            dispatcher.listen('popup:rendered', closeHandler);
            cleanup.push(() => dispatcher.removeListener('popup:rendered', closeHandler));
        }

        if (!isOverlay && modalRef) {
            const clickOutside = (event: MouseEvent) => {
                if (!modalRef.current?.contains(event.target as Node)) {
                    closeHandler();
                }
            };

            document.addEventListener('mousedown', clickOutside);
            cleanup.push(() => document.removeEventListener('mousedown', clickOutside));
        }
        return () => {
            cleanup.map((cleanupFunc) => cleanupFunc());
        };
    }, [closeHandler, closeOnModalEvent, isOverlay, modalRef]);

    if (!active) {
        return null;
    }

    return isOverlay ? <Overlay onClick={closeHandler} style={{ position: 'fixed' }} /> : null;
};

export default useClickOutside;
